/**
 * ZonesContext — real implementation (Phase 3 Step 3).
 *
 * Fetches zones from backend via tRPC zones.list (authenticated).
 * Persists selected zone ID in localStorage under melodiza:current-zone-id.
 *
 * Previous stub: zones=[], currentZone=null.
 * Now: real zones from Drizzle/DO backend + currentZoneId persistence.
 */
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { trpc } from '../lib/trpc';
import { useAuth } from './AuthContext';
import { useLocalZones } from '../lib/localZones';

const ZonesContext = createContext(null);

export function ZonesProvider({ children }) {
  const { user } = useAuth();
  const [currentZoneId, setCurrentZoneId] = useState(() => {
    try {
      return localStorage.getItem('melodiza:current-zone-id') || null;
    } catch {
      return null;
    }
  });

  // tRPC query — only enabled when user is authenticated.
  // Backend may be unavailable (no profile, env, etc.) — we don't block UI on it.
  const zonesQuery = trpc.zones.list.useQuery(undefined, {
    enabled: !!user,
    staleTime: 30_000,
    retry: false,
  });
  const remoteZones = zonesQuery.data?.zones ?? [];
  const localZones = useLocalZones();

  // Merge: backend zones first, then local zones not present remotely (by id).
  const zones = useMemo(() => {
    const remoteIds = new Set(remoteZones.map((z) => z.id));
    const onlyLocal = localZones.filter((z) => !remoteIds.has(z.id));
    return [...remoteZones, ...onlyLocal];
  }, [remoteZones, localZones]);

  // Persist currentZoneId to localStorage
  useEffect(() => {
    try {
      if (currentZoneId) {
        localStorage.setItem('melodiza:current-zone-id', currentZoneId);
      } else {
        localStorage.removeItem('melodiza:current-zone-id');
      }
    } catch {}
  }, [currentZoneId]);

  const currentZone = useMemo(
    () => zones.find((z) => z.id === currentZoneId) ?? null,
    [zones, currentZoneId]
  );

  const value = useMemo(
    () => ({
      zones,
      currentZone,
      currentZoneId,
      setCurrentZoneId,
      isLoading: zonesQuery.isLoading,
      refetch: zonesQuery.refetch,
    }),
    [
      zones,
      currentZone,
      currentZoneId,
      zonesQuery.isLoading,
      zonesQuery.refetch,
    ]
  );

  return (
    <ZonesContext.Provider value={value}>{children}</ZonesContext.Provider>
  );
}

export function useZones() {
  const ctx = useContext(ZonesContext);
  if (!ctx) throw new Error('useZones must be inside ZonesProvider');
  return ctx;
}
